import { Show, createMemo, type Component } from 'solid-js';
import type { SpreadAnalysis } from '~/domain/types';
import { formatCents, payoutDeviations } from '~/domain/profiles';
import { standardHouseEdge } from '~/domain/spread';

/**
 * The §9 panel. First-class UI, never buried.
 *
 * The edge shown is *computed from the active table*, and the standard-rules
 * figure sits beside it so a table with shaved odds reads as a gap between two
 * numbers rather than hiding behind a constant. Nothing here may imply that any
 * arrangement of bets moves the mean — only the odds do.
 */

export interface HonestyPanelProps {
  analysis: SpreadAnalysis;
  currency: string;
}

const pct = (value: number) => `${(value * 100).toFixed(2)}%`;

const HonestyPanel: Component<HonestyPanelProps> = (props) => {
  const table = () => props.analysis.table;
  const staked = () => props.analysis.totalStakeCents > 0;

  const standard = createMemo(() => standardHouseEdge(props.analysis.wheel.style));
  const actual = () => props.analysis.expectedValuePct;
  const worse = () => staked() && actual() < standard() - 1e-9;
  const better = () => staked() && actual() > standard() + 1e-9;

  const evChips = () =>
    props.analysis.expectedValue / (table().baseUnit || 1);

  const deviations = createMemo(() => payoutDeviations(table()));

  return (
    <section class="rounded-lg border border-line bg-surface-1 p-3">
      <h2 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Expected value
      </h2>

      <Show
        when={staked()}
        fallback={
          <p class="text-[11px] text-ink-faint">
            Place a bet to see what it costs per spin.
          </p>
        }
      >
        <div class="flex items-baseline gap-2">
          <span
            class="tnum text-[26px] font-semibold leading-none"
            classList={{ 'text-loss': actual() < 0, 'text-ink': actual() >= 0 }}
          >
            {pct(actual())}
          </span>
          <span class="text-[11px] text-ink-dim">of stake, every spin</span>
        </div>

        <dl class="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          <div>
            <dt class="text-ink-faint">Per spin</dt>
            <dd class="tnum text-ink">
              {formatCents(Math.round(props.analysis.expectedValue), props.currency)}
              <span class="ml-1 text-ink-faint">({evChips().toFixed(3)} chips)</span>
            </dd>
          </div>
          <div>
            <dt class="text-ink-faint">Per 100 spins</dt>
            <dd class="tnum text-ink">
              {formatCents(Math.round(props.analysis.expectedValue * 100), props.currency)}
            </dd>
          </div>
        </dl>

        {/* The standard figure, always beside the computed one. */}
        <div class="mt-2 flex items-center justify-between border-t border-line pt-2 text-[11px]">
          <span class="text-ink-faint">
            Standard {props.analysis.wheel.label.toLowerCase()} edge
          </span>
          <span class="tnum text-ink-dim">{pct(standard())}</span>
        </div>

        <Show when={worse()}>
          <p class="mt-1.5 rounded border border-loss/40 bg-loss/10 p-2 text-[11px] text-loss">
            This table is <strong>worse than standard</strong> by{' '}
            {pct(Math.abs(actual() - standard()))} of stake.
            <Show when={deviations().length > 0}>
              {' '}
              {deviations()
                .map((d) => `${d.betType} pays ${d.actual}:1 instead of ${d.standard}:1`)
                .join('; ')}
              .
            </Show>
          </p>
        </Show>

        <Show when={better()}>
          <p class="mt-1.5 rounded border border-gain/40 bg-gain/10 p-2 text-[11px] text-gain">
            This table is better than standard by {pct(Math.abs(actual() - standard()))} of
            stake
            <Show when={table().zeroRule !== 'none'}>, because {table().zeroRule} returns
            half of a losing even-money stake when a zero hits</Show>.
          </p>
        </Show>

        <Show when={table().zeroRule === 'en-prison'}>
          <p class="mt-1.5 text-[10px] leading-relaxed text-ink-faint">
            En prison has the same mean as la partage but a different spread of outcomes.
            This figure assumes a stake meeting a second zero stays imprisoned. Simulation
            resolves it spin by spin.
          </p>
        </Show>

        <p class="mt-2 border-t border-line pt-2 text-[10px] leading-relaxed text-ink-faint">
          Spreading a stake over more pockets changes the <em>shape</em> of the outcome
          distribution. It does not change this number. Only the odds on the table do.
        </p>
      </Show>
    </section>
  );
};

export default HonestyPanel;
